import { Link } from 'react-router-dom';
import { Icon, type IconName } from '../components/Icon.js';

// "מדריך" — read-only reference for the sync-group keyword commands (mirrors bizz-pilot's מדריך tab).
// Documents the keywords the sync-command parser supports (packages/shared/src/broadcast/syncCommand.ts).
// Pure presentation: no data fetching, no side effects. Grouped like the competitor, in our design system.

interface Cmd {
  /** The keyword the admin types on the FIRST line of the sync-group message. */
  key: string;
  /** One plain-Hebrew sentence: what it does. */
  desc: string;
  /** Optional tiny example (rendered mono, LTR-safe for the HH:MM parts). */
  example?: string;
  /** True for reply-based commands (operate on a quoted, already-published message). */
  reply?: boolean;
}

interface Group {
  title: string;
  icon: IconName;
  items: Cmd[];
}

const GROUPS: Group[] = [
  {
    title: 'שליחה',
    icon: 'send',
    items: [
      { key: 'שידור', desc: 'שולח את ההודעה לכל קבוצות היעד שלך, עם הסיומת הקבועה.', example: 'שידור\nהתוכן בשורה הבאה' },
      { key: 'ללא סיומת', desc: 'שולח לכל הקבוצות בלי לצרף את הסיומת הקבועה.' },
      { key: 'תצוגה', desc: 'שולח עם תצוגה מקדימה של הקישור שבהודעה.' },
      { key: 'סטטוס!', desc: 'מפרסם את התוכן כסטטוס (Story) במקום כהודעה בקבוצה.' },
    ],
  },
  {
    title: 'תזמון',
    icon: 'clock',
    items: [
      { key: 'תזמן HH:MM', desc: 'מתזמן שליחה לשעה שנתת. שורה 1 הפקודה, שורה 2 והלאה התוכן.', example: 'תזמן 14:30\nהתוכן בשורה הבאה' },
      { key: 'תזמן סטטוס HH:MM', desc: 'מתזמן פרסום כסטטוס בשעה שנתת.', example: 'תזמן סטטוס 09:00' },
      { key: 'תזמן תצוגה HH:MM', desc: 'מתזמן שליחה עם תצוגה מקדימה של קישור בשעה שנתת.', example: 'תזמן תצוגה 18:15' },
      { key: 'בטל תזמון', desc: 'מבטל תזמון קיים.' },
      { key: 'כל התזמונים', desc: 'מציג את רשימת התזמונים הפעילים.' },
    ],
  },
  {
    title: 'עריכה ומעקב',
    icon: 'composer',
    items: [
      { key: 'עריכה!', desc: 'מעדכן הודעה שכבר פורסמה — בכל היעדים בבת אחת. הטקסט החדש בשורות הבאות.', reply: true },
      { key: 'פרטים!', desc: 'מחזיר דוח צפיות להודעה שפורסמה.', reply: true },
      { key: 'הצמדת הודעה!', desc: 'מצמיד את ההודעה לראש כל הקבוצות למשך 7 ימים.', reply: true },
    ],
  },
  {
    title: 'ניהול קבוצות',
    icon: 'users',
    items: [
      { key: 'תיאור קבוצה', desc: 'מעדכן את התיאור בכל קבוצות היעד. התיאור החדש בשורות הבאות.' },
      { key: 'החלפת תמונה!', desc: 'שולח תמונה עם הפקודה ככיתוב — והיא נקבעת כתמונת הפרופיל בכל הקבוצות.' },
      { key: 'הוספת מנהל / הסרת מנהל', desc: 'מוסיף או מסיר מנהל בכל הקבוצות, לפי מספר טלפון שרשום בהודעה.' },
      { key: 'הוספת חבר! / הסרת חבר!', desc: 'מוסיף או מסיר חבר בכל הקבוצות, לפי מספר טלפון שרשום בהודעה.' },
    ],
  },
  {
    title: 'סקרים ומידע',
    icon: 'list',
    items: [
      { key: 'סקר!', desc: 'יוצר סקר בכל הקבוצות. שורה 2 השאלה, השורות הבאות האפשרויות.', example: 'סקר!\nמתי נוח לכם?\nבוקר\nערב' },
      { key: 'חשיפה', desc: 'מחזיר את סך המשתתפים בכל קבוצות היעד.' },
      { key: 'קישור!', desc: 'שולח אליך בפרטי (DM) את קישורי ההצטרפות לכל הקבוצות.' },
    ],
  },
  {
    title: 'תוכן',
    icon: 'download',
    items: [
      { key: 'כתבה!', desc: 'שולחים קישור לכתבה — ומקבלים בחזרה את הטקסט המלא שלה.', example: 'כתבה!\nhttps://…' },
    ],
  },
];

const badgeStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 600,
  color: 'var(--accent-teal)', background: 'color-mix(in srgb, var(--accent-teal) 12%, transparent)',
  padding: '4px 10px', borderRadius: 'var(--pill)', whiteSpace: 'nowrap',
};

function GroupCard({ group }: { group: Group }): JSX.Element {
  return (
    <div className="card" style={{ padding: 24, marginBottom: 20 }}>
      <h3 style={{ fontSize: 18, fontWeight: 700, fontFamily: 'var(--font-display)', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <Icon name={group.icon} /> {group.title}
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {group.items.map((c) => (
          <div key={c.key} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
              <span style={badgeStyle} dir="ltr">{c.key}</span>
              {c.reply && <span className="chip muted">בתגובה להודעה</span>}
            </div>
            <div style={{ flex: '1 1 260px', minWidth: 240 }}>
              <div style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.5 }}>{c.desc}</div>
              {c.example && (
                <div style={{ marginTop: 6, fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-dim)', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                  {c.example}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function GuidePage(): JSX.Element {
  return (
    <div className="content">
      <div className="page-head">
        <div>
          <div className="eyebrow">מדריך</div>
          <h1>מדריך מילות מפתח</h1>
          <div className="sub">כתוב את מילת המפתח בשורה הראשונה של ההודעה בקבוצת השליטה שלך — המערכת תזהה ותבצע</div>
        </div>
      </div>

      <div className="card" style={{ padding: 20, marginBottom: 20, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <Icon name="bolt" />
        <div style={{ fontSize: 14, color: 'var(--text-dim)', lineHeight: 1.6 }}>
          כדי שהפקודות יעבדו צריך קודם להגדיר את קבוצת השליטה (הסנכרון) ואת קבוצות היעד —
          זה נעשה בעמוד{' '}
          <Link to="/sessions" style={{ color: 'var(--accent-teal)', fontWeight: 600 }}>המספרים והקבוצות</Link>.
          {' '}הפקודות שמסומנות "בתגובה להודעה" נכתבות כ-reply על הודעה שכבר פורסמה.
        </div>
      </div>

      {GROUPS.map((g) => <GroupCard key={g.title} group={g} />)}
    </div>
  );
}
